import { useEffect } from "react";
import { useLocation } from "react-router";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/**
 * Hash-scroll + arrival focus/highlight for the dashboard shell.
 *
 * Mounted ONCE in AppShell and now the SINGLE handler for both same-page AND
 * cross-page `/page#section` deep-links (command palette, sidebar, mobile nav):
 * every deep-link routes through `navigate(path)`, and this hook scrolls the
 * target into view, moves focus to it, and flashes the arrival highlight. React
 * Router does not hash-scroll on navigate and there is no other global handler,
 * so without this the deep-link lands at page top.
 *
 * The effect keys on `location.key` as well as the hash/pathname so that
 * re-navigating to the SAME section (identical hash → a router push with a fresh
 * `location.key`) re-fires the scroll+focus+highlight instead of no-op'ing.
 *
 * No-op on non-scroll hashes: a hash with no matching element id (e.g. the
 * streaming flyout modes `#settings`/`#analytics`/`#logs`, which the flyout host
 * consumes and clears) resolves to null and is skipped — never throws, never
 * hijacks a page that manages its own hash/tab state.
 *
 * Lazy/async timing: on a cross-page nav the target may sit behind a
 * Suspense-lazy route or an SSE-gated section (Software RAID / Hardware Health)
 * that mounts after any fixed budget, so after an initial rAF attempt we wait
 * for it via a `MutationObserver` on `document.body` — no time cutoff. We scroll
 * exactly once and release the observer on success. Self-cleaning on unmount or
 * hash change.
 */
export const useHashScroll = () => {
  const location = useLocation();
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    if (!id) return;

    const behavior: ScrollBehavior = prefersReducedMotion ? "auto" : "smooth";
    let raf = 0;
    let mutationObserver: MutationObserver | null = null;
    let landed = false;
    let highlightTimer = 0;
    let highlighted: HTMLElement | null = null;

    const land = (el: HTMLElement) => {
      el.scrollIntoView({ behavior, block: "start" });

      // Move keyboard focus onto the landed section (its heading if it has one)
      // so the next Tab continues from here, not the page top. A non-focusable
      // target gets tabindex="-1" — programmatically focusable, never added to
      // the tab order. preventScroll keeps our scrollIntoView position.
      const heading = el.querySelector<HTMLElement>("h1, h2, h3");
      const focusTarget = heading ?? el;
      if (
        !focusTarget.hasAttribute("tabindex") &&
        !focusTarget.matches("a[href], button, input, select, textarea, [contenteditable]")
      ) {
        focusTarget.setAttribute("tabindex", "-1");
      }
      focusTarget.focus({ preventScroll: true });

      // Brief highlight so the eye finds the section. The ring pulse (and its
      // reduced-motion static fallback) live on .qb-anchor-target in index.css.
      el.classList.add("qb-anchor-target");
      highlighted = el;
      highlightTimer = window.setTimeout(() => {
        el.classList.remove("qb-anchor-target");
        highlighted = null;
      }, 1600);
    };

    const tryLand = (): boolean => {
      if (landed) return true;
      const el = document.getElementById(id);
      if (!el) return false;
      landed = true;
      mutationObserver?.disconnect();
      mutationObserver = null;
      land(el);
      return true;
    };

    raf = window.requestAnimationFrame(() => {
      raf = 0;
      if (tryLand()) return;
      // Target sits behind a lazy route or an SSE-gated section that mounts
      // later; wait for it via the DOM instead of a time budget. Coalesced to
      // one lookup per frame; scrolls exactly once.
      mutationObserver = new MutationObserver(() => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          tryLand();
        });
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      mutationObserver?.disconnect();
      if (highlightTimer) window.clearTimeout(highlightTimer);
      if (highlighted) highlighted.classList.remove("qb-anchor-target");
    };
  }, [location.hash, location.pathname, location.key, prefersReducedMotion]);
};
